import { PromotionCodeInfo } from '../../types/rest-api';
import { formatChf } from '../../utils/currency-formatter';

/**
 * Calculates the CHF discount a Stripe-style promotion code gives for a
 * prepaid subscription of `subscriptionMonths` months, billed at
 * `monthlyNetRate` CHF/month (already reflecting any contract-length discount).
 *
 * `totalBeforePromo` is the CHF total the customer would otherwise pay and is
 * used as the base for `once` / `forever` percent-off codes.
 *
 * `amount_off` is expected in the smallest currency unit (Rappen → ÷100).
 */
export function calcPromoDiscount(
  promo: PromotionCodeInfo | null,
  totalBeforePromo: number,
  monthlyNetRate: number,
  subscriptionMonths: number
): number {
  if (!promo) return 0;

  const percent = promo.percent_off;
  const amountChf = promo.amount_off != null ? promo.amount_off / 100 : null;

  if (promo.duration === 'repeating' && promo.duration_in_months != null) {
    // "Erste N Monate kostenlos" is a trial, not a discount: the backend
    // starts the subscription with a trial and Stripe bills the full term
    // when it ends, so the order total stays the regular price.
    if (percent === 100) return 0;
    const months = Math.min(promo.duration_in_months, subscriptionMonths);
    if (percent != null) return monthlyNetRate * months * (percent / 100);
    if (amountChf != null) return amountChf * months;
    return 0;
  }

  if (promo.duration === 'forever') {
    if (percent != null) return totalBeforePromo * (percent / 100);
    if (amountChf != null) return amountChf * subscriptionMonths;
    return 0;
  }

  if (percent != null) return totalBeforePromo * (percent / 100);
  if (amountChf != null) return amountChf;
  return 0;
}

/** Human-readable label for the promo line in the order summary (German). */
export function promoDiscountLabel(promo: PromotionCodeInfo | null): string {
  if (!promo) return '';

  const percent = promo.percent_off;
  const amountChf = promo.amount_off != null ? promo.amount_off / 100 : null;

  if (promo.duration === 'repeating' && promo.duration_in_months != null) {
    const months = promo.duration_in_months;
    const monthWord = months === 1 ? 'Monat' : 'Monate';
    if (percent === 100) return `Erste ${months} ${monthWord} kostenlos`;
    if (percent != null) return `${percent}% Rabatt (${months} ${monthWord})`;
    if (amountChf != null)
      return `${formatChf(amountChf)} Rabatt pro Monat (${months} ${monthWord})`;
  }

  if (promo.duration === 'forever') {
    if (percent != null) return `Dauerrabatt (${percent}%)`;
    if (amountChf != null)
      return `Dauerrabatt (${formatChf(amountChf)} / Monat)`;
  }

  if (percent != null) return `Einmaliger Rabatt (${percent}%)`;
  if (amountChf != null)
    return `Einmaliger Rabatt (${formatChf(amountChf)} CHF)`;

  return 'Aktionscode';
}
